
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import AddProductsHeader from "../../components/Products/AddProductsHeader";
import { toast, ToastContainer } from "react-toastify";
import ProductForm from "../../components/Products/ProductForm";
import ProductPhotos from "../../components/Products/ProductPhotos";
import SuccessPopUp from "../../components/Products/SuccessPopUp";
import { useSuppliers } from "../../hooks/useSuppliers";
import { useProduct } from "../../hooks/useProduct";
import { useTranslation } from "react-i18next";
import { API_URL } from "../../utils/config";
import { useAuth } from "../../contexts/AuthProvider";
const baseUrl = API_URL;
// ─── تحديث هنا: أضفنا sku إلى الـstate الابتدائي ───────────────────────────────
const initialFormState = {
  sku: "",               // ← حقل SKU
  name: "",
  name_ar: "",
  type: "",
  offer_type: "",
  description: "",
  description_ar: "",
  price: "",
  shipping_price: "",
  shipping_period: "",
  minimum_order: "",
  quantity: "",
  supplier_id: "",
  active: true,
  customized: false, // ← حقل لتحديد إذا كان المنتج مخصصًا
};

function EditProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation("productlist");

  const {
    suppliers,
    loading: suppliersLoading,
    error: suppliersError,
  } = useSuppliers();

  const {
    product,
    oldImagesFiles,
    loading: productLoading,
    error: productError,
  } = useProduct();

  const [formData, setFormData] = useState(initialFormState);
  const [prevFormData, setPrevFormData] = useState({});
  const [adding, setAdding] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [imageFocus, setImageFocus] = useState({});
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    if (product) {
      setFormData(product);
      setPrevFormData(product);
      setImagesToRemove(oldImagesFiles || []);
    }
  }, [product, oldImagesFiles]);
  console.log("Form Data:", formData);

  useEffect(() => {
    const isValid =
      Object.values(formData).every(
        (value) => String(value).trim() !== "" && value !== "0"
      ) &&
      formData.description.length >= 10 &&
      formData.name.length >= 3 &&
      formData.name_ar.length >= 3 &&
      formData.description_ar.length >= 10 &&
      (imagesToRemove?.length || 0) + newImages.length > 0;

    setIsFormValid(isValid);
  }, [formData, prevFormData, removedImages, imagesToRemove, newImages]);

 const handleSubmit = async (e) => {
  e.preventDefault();
  if (adding) return;
  setAdding(true);

  const hasFormChanges = Object.keys(formData).some(
    (key) => String(formData[key]) !== String(prevFormData[key])
  );

  if (!hasFormChanges && removedImages.length === 0 && newImages.length === 0) {
    toast.warning(t("noChange"), { autoClose: 3000 });
    setAdding(false);
    return;
  }

  const loadingToast = toast.loading(t("processingEdit"));

  if (!isAuthenticated) {
    localStorage.clear();
    navigate("/login");
    setAdding(false);
    return;
  }

  const formDataProduct = new FormData();

  // Add all form fields that have changed
  Object.entries(formData).forEach(([key, value]) => {
    if (String(formData[key]) !== String(prevFormData[key])) {
      formDataProduct.append(key, value);
    }
  });

  // Add new images
  newImages.forEach((image) => formDataProduct.append("images", image));
  
  // Add removed images
  removedImages.forEach((image) =>
    formDataProduct.append("removed_images", image)
  );
  // if customized 
  const FetchUrl = formData.customized
    ? `${baseUrl}/api/v1/attnd-supplier/services/custom-update/${id}`
    : `${baseUrl}/api/v1/attnd-supplier/services/${id}`;

  try {
    const response = await fetch(FetchUrl, {
      method: "PUT",
        credentials: "include",
        headers: { 
          // Remove Content-Type header when using FormData
          // FormData automatically sets the correct Content-Type with boundary
          "lang": i18n.language
        },
        body: formDataProduct,
      }
    );

    if (response.status === 401) {
      localStorage.clear();
      navigate("/login");
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Error submitting product:", errorData);
      
      // Handle structured error response
      if (errorData.errors) {
        let errorMessages = '';
        
        if (Array.isArray(errorData.errors)) {
          // Handle array of error objects
          errorMessages = errorData.errors
            .map(errorObj => 
              Object.entries(errorObj)
                .map(([field, message]) => `${field}: ${message}`)
                .join(', ')
            )
            .join(', ');
        } else {
          // Handle direct error object
          errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join(', ');
        }
        
        throw new Error(errorMessages);
      } else {
        // Handle simple error message
        throw new Error(errorData.message || errorData.error || t("unknownError"));
      }
    }

    toast.update(loadingToast, {
      render: <SuccessPopUp addOrEdit="Edited" />,
      type: "success",
      autoClose: 3000,
      icon: false,
      style: {
        width: "340px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fff",
      },
    });

    setTimeout(() => navigate(-1), 3000);
  } catch (error) {
    setAdding(false);
    toast.dismiss(loadingToast);
    toast.error(error.message, { autoClose: 3000 });
    console.error(t("submitError"), error);
  }
};

  if (suppliersError || productError) {
    return <h1>{suppliersError || productError}</h1>;
  }

  if (suppliersLoading || productLoading || !product || !suppliers) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 my-16 mx-5">
      <AddProductsHeader 
      title={t("editProduct")} isCustomProduct={formData.customized} isEdit={true} />
      <ProductPhotos
        images={newImages}
        setImages={setNewImages}
        oldImages={imagesToRemove}
        removedImages={removedImages}
        setImagesToRemove={setImagesToRemove}
        imagesToRemove={imagesToRemove}
        setRemovedImages={setRemovedImages}
        isEditMode={true}
        imageFocus={imageFocus}
        setImageFocus={setImageFocus}
      />
      <ProductForm
        formData={formData}
        isCustomProduct={formData.customized || formData.price === null } 
        isFormValid={isFormValid}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        suppliers={suppliers}
        isEdit={true}
      />
      <ToastContainer position="bottom-right" />
    </div>
  );
}

export default EditProduct;